import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { useLanguage } from '../lib/LanguageContext'

// Brand palette matched to Tailwind tokens
const COLORS = {
  primary:   '#2E7D9B',
  success:   '#16a34a',
  warning:   '#d97706',
  danger:    '#dc2626',
  muted:     '#9ca3af',
  secondary: '#6b7280',
}

// New-patient visit-type badge colours (Paul 2026-07-07)
const TYPE_BADGE = {
  emergency: 'bg-red-50 text-red-700',
  cosmetic:  'bg-amber-50 text-amber-700',
  general:   'bg-gray-100 text-gray-600',
}

const STATUS_COLORS = {
  scheduled:   COLORS.primary,
  confirmed:   COLORS.success,
  completed:   '#6366f1',
  cancelled:   COLORS.danger,
  no_show:     COLORS.muted,
  rescheduled: COLORS.warning,
  pending_confirmation: '#f97316',
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-brand-border bg-white px-3 py-2 shadow-md text-xs">
      {label && <p className="mb-1 font-semibold text-brand-ink">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }} className="font-medium">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  )
}

export default function Analytics({
  cancellation_stats,
  booking_stats,
  channel_stats,
  daily_bookings = [],
  status_distribution = [],
  todays_bookings = null,
  conversation_outcomes = null,
}) {
  const { t } = useLanguage()

  const channelData = [
    { name: 'WhatsApp', value: channel_stats?.whatsapp ?? 0, fill: COLORS.success },
    { name: 'Voice',    value: channel_stats?.voice    ?? 0, fill: COLORS.primary },
  ]

  return (
    <DashboardLayout>
      <div className="mb-8">
        <span className="inline-flex items-center rounded-full border border-brand-accent bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-primary">
          {t('ana_badge')}
        </span>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-brand-ink">{t('ana_title')}</h1>
        <p className="mt-2 text-sm leading-6 text-brand-muted">{t('ana_subtitle')}</p>
      </div>

      {/* ── Today's bookings (Paul 2026-06-26) ───────────────────── */}
      <TodaysBookings data={todays_bookings} />

      {/* ── Conversation outcomes (WhatsApp booking funnel) ──────── */}
      {conversation_outcomes && (
        <ConversationOutcomes data={conversation_outcomes} />
      )}

      {/* ── KPI row ──────────────────────────────────────────────── */}
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard
          label={t('ana_total_bookings')}
          value={booking_stats?.total_bookings_30d ?? 0}
          color="text-brand-primary"
          bg="bg-brand-primary/10"
        />
        <KpiCard
          label={t('ana_completed')}
          value={booking_stats?.completed_30d ?? 0}
          color="text-brand-success"
          bg="bg-brand-success/10"
        />
        <KpiCard
          label={t('ana_no_shows')}
          value={booking_stats?.no_shows_30d ?? 0}
          color="text-brand-danger"
          bg="bg-brand-danger/10"
        />
        <KpiCard
          label={t('ana_conversion')}
          value={`${booking_stats?.conversion_rate ?? 0}%`}
          color="text-brand-primary"
          bg="bg-brand-primary/10"
        />
      </div>

      {/* ── Daily bookings bar chart ─────────────────────────────── */}
      <div className="mb-5 rounded-xl border border-brand-accent/75 bg-white p-6 shadow-sm">
        <h2 className="mb-1 text-base font-semibold text-brand-ink">Bookings — last 30 days</h2>
        <p className="mb-4 text-xs text-brand-muted">New appointments created per day</p>
        {daily_bookings.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={daily_bookings} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                interval={4}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f3f4f6' }} />
              <Bar dataKey="count" name="Bookings" fill={COLORS.primary} radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart />
        )}
      </div>

      {/* ── Status distribution + Channel split ─────────────────── */}
      <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Status donut */}
        <div className="rounded-xl border border-brand-accent/75 bg-white p-6 shadow-sm">
          <h2 className="mb-1 text-base font-semibold text-brand-ink">Appointment status</h2>
          <p className="mb-4 text-xs text-brand-muted">All-time breakdown by status</p>
          {status_distribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={status_distribution}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                >
                  {status_distribution.map((entry, i) => {
                    const raw = entry.name.toLowerCase().replace(' ', '_')
                    return (
                      <Cell
                        key={i}
                        fill={STATUS_COLORS[raw] || Object.values(COLORS)[i % Object.values(COLORS).length]}
                      />
                    )
                  })}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(v) => <span className="text-xs text-brand-muted">{v}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </div>

        {/* Channel split bar */}
        <div className="rounded-xl border border-brand-accent/75 bg-white p-6 shadow-sm">
          <h2 className="mb-1 text-base font-semibold text-brand-ink">{t('ana_channel_title')}</h2>
          <p className="mb-4 text-xs text-brand-muted">Total conversations per channel</p>
          {channelData.some((d) => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={channelData} layout="vertical" margin={{ top: 4, right: 24, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#374151' }} tickLine={false} axisLine={false} width={68} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f3f4f6' }} />
                <Bar dataKey="value" name="Conversations" radius={[0, 4, 4, 0]} maxBarSize={32}>
                  {channelData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </div>
      </div>

      {/* ── Cancellation reasons ─────────────────────────────────── */}
      <div className="rounded-xl border border-brand-accent/75 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-4">
          <div>
            <h2 className="text-base font-semibold text-brand-ink">{t('ana_cancel_title')}</h2>
            <p className="mt-0.5 text-xs text-brand-muted">
              {t('ana_total_cancelled')}{' '}
              <span className="font-semibold text-brand-danger">{cancellation_stats?.total_cancelled ?? 0}</span>
              {'  '}·{'  '}
              {t('ana_rate')}{' '}
              <span className="font-semibold text-brand-danger">{cancellation_stats?.cancellation_rate ?? 0}%</span>
            </p>
          </div>
        </div>

        {(cancellation_stats?.by_reason?.length ?? 0) > 0 ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart
              data={cancellation_stats.by_reason}
              layout="vertical"
              margin={{ top: 0, right: 24, left: 100, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="category"
                tick={{ fontSize: 11, fill: '#374151' }}
                tickLine={false}
                axisLine={false}
                width={96}
                tickFormatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f3f4f6' }} />
              <Bar dataKey="count" name="Cases" fill={COLORS.danger} radius={[0, 4, 4, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="py-8 text-center text-sm text-brand-muted">No cancellation data yet</p>
        )}
      </div>

      {/* ── Open leads — moved to the bottom (Paul 2026-06-26) ────── */}
      {conversation_outcomes && (
        <OpenLeadsPanel data={conversation_outcomes} />
      )}
    </DashboardLayout>
  )
}

// Conversation-outcome funnel + the at-risk "open leads" list — the lost-patient catcher.
function ConversationOutcomes({ data }) {
  const t = data.totals || {}
  const r = data.rates || {}
  const leads = data.open_leads || []

  const fmtRate = (v) => (v == null ? '—' : `${v}%`)
  const convColor =
    (r.booking_conversion ?? 0) >= 50 ? 'text-brand-success'
      : (r.booking_conversion ?? 0) >= 30 ? 'text-brand-primary'
        : 'text-brand-danger'

  return (
    <div className="mb-6 rounded-xl border border-brand-accent/75 bg-white p-6 shadow-sm">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-brand-ink">WhatsApp booking funnel</h2>
          <p className="mt-0.5 text-xs text-brand-muted">
            Last {data.window_days ?? 30} days · of patients who messaged us wanting to book, how many we converted — and who we&apos;re at risk of losing.
          </p>
        </div>
        <span className="text-[11px] text-brand-muted">Booking conversion benchmark: {data.benchmarks?.booking_conversion}</span>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <FunnelStat label="Booking enquiries" value={t.booking_intent ?? 0} sub="showed intent" />
        <FunnelStat label="Booked" value={t.booked ?? 0} sub="completed booking" color="text-brand-success" />
        <FunnelStat label="Conversion" value={fmtRate(r.booking_conversion)} sub="booked / enquiries" color={convColor} />
        <FunnelStat label="Abandoned" value={t.abandoned ?? 0} sub={`${fmtRate(r.abandonment)} of enquiries`} color="text-brand-danger" />
        <FunnelStat label="Re-ask rate" value={fmtRate(r.re_ask)} sub="asked twice for same info" color={(r.re_ask ?? 0) > 0 ? 'text-brand-warning' : 'text-brand-success'} />
        <FunnelStat label="After-hours" value={fmtRate(r.after_hours_conversion)} sub={`conv · ${fmtRate(r.after_hours_share)} share`} />
      </div>

    </div>
  )
}

// Open leads — its own panel, rendered at the BOTTOM of the page (Paul 2026-06-26) so the daily
// bookings + funnel lead, and the long at-risk list no longer pushes everything down.
function OpenLeadsPanel({ data }) {
  const leads = data.open_leads || []
  return (
    <div className="mb-6 rounded-xl border border-brand-danger/30 bg-brand-danger/5 p-6 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-brand-ink">
          🔴 Open leads — at risk of being lost ({data.open_leads_count ?? leads.length})
        </h3>
        <span className="text-[11px] text-brand-muted">we have the last word · no booking · review daily</span>
      </div>
      {leads.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-brand-muted">
                <th className="py-1 pr-3 font-medium">Patient</th>
                <th className="py-1 pr-3 font-medium">Phone</th>
                <th className="py-1 pr-3 font-medium">Idle</th>
                <th className="py-1 font-medium">What they wanted</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.conversation_id} className="border-t border-brand-danger/15">
                  <td className="py-1.5 pr-3 font-medium text-brand-ink">{l.name}</td>
                  <td className="py-1.5 pr-3 text-brand-muted">{l.phone || '—'}</td>
                  <td className="py-1.5 pr-3 text-brand-muted">{l.idle_hours != null ? `${l.idle_hours}h` : '—'}</td>
                  <td className="py-1.5 text-brand-secondary">{l.intent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="py-3 text-center text-xs text-brand-success">No open leads — every booking enquiry has been handled. ✓</p>
      )}
    </div>
  )
}

// Bookings MADE today — how many entries went INTO the diary on this day (whatever future day they're
// for), split by NEW vs EXISTING patient and by channel (WhatsApp vs direct). Paul 2026-06-26.
function TodaysBookings({ data }) {
  if (!data) return null
  const rows = data.rows || []
  const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) }
  const [period, setPeriod] = useState(data.period || 'day')
  const [anchor, setAnchor] = useState(data.anchor || todayISO())
  // Which period to compare against. 'previous' = yesterday / prev week / prev month; 'same_dow'
  // = the same weekday last week; 'same_dow_4wk' = the same weekday 4 weeks ago (both day mode only —
  // the 4-week one gives a time-of-month like-for-like). Reset to 'previous' whenever the period changes.
  const [baseKey, setBaseKey] = useState('previous')
  const reload = (p, a) => router.get('/analytics', { booking_period: p, booking_date: a },
    { only: ['todays_bookings'], preserveScroll: true, preserveState: true, replace: true })
  const setP = (p) => { const a = todayISO(); setPeriod(p); setAnchor(a); setBaseKey('previous'); reload(p, a) }
  const step = (dir) => {
    const d = new Date(anchor + 'T00:00:00')
    if (period === 'month') d.setMonth(d.getMonth() + dir)
    else d.setDate(d.getDate() + (period === 'week' ? 7 : 1) * dir)
    const a = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
    setAnchor(a); reload(period, a)
  }
  const periodLabel = period === 'day' ? 'Day' : period === 'week' ? 'Week' : 'Month'
  // The selected comparison baseline (may be null if the server didn't ship one) and its type mix.
  const base =
    baseKey === 'same_dow' ? data.same_dow_last_week
      : baseKey === 'same_dow_4wk' ? data.same_dow_4wk
        : data.previous
  const bt = base?.new_by_type || {}
  const nt = data.new_by_type || {}
  // Compare on the ACTUAL (net-of-cancellation) figures so the delta matches the big number shown.
  const curNew = data.new_actual ?? data.new_count ?? 0
  const baseNew = base ? (base.new_actual ?? base.new_count ?? 0) : null
  const curExisting = data.existing_actual ?? data.existing_count ?? 0
  const baseExisting = base ? (base.existing_actual ?? base.existing_count ?? 0) : null
  return (
    <div className="mb-6 rounded-xl border border-brand-accent/75 bg-white p-6 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-brand-ink">Bookings made — {data.label}</h2>
          <p className="mt-0.5 text-xs text-brand-muted">Appointments ENTERED into the diary in this period (whatever day they’re for) — new vs existing patients, and how they booked.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-lg border border-brand-border">
            {[['day', 'Today'], ['week', 'Week'], ['month', 'Month']].map(([p, lbl]) => (
              <button key={p} type="button" onClick={() => setP(p)}
                className={`px-3 py-1.5 text-xs font-medium transition ${period === p ? 'bg-brand-primary text-white' : 'bg-white text-brand-muted hover:bg-brand-surface'}`}>
                {lbl}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 rounded-lg border border-brand-border px-1 py-0.5">
            <button type="button" onClick={() => step(-1)} title={`Previous ${periodLabel.toLowerCase()}`} className="rounded p-1 text-brand-muted hover:bg-brand-surface"><ChevronLeft size={15} /></button>
            <span className="min-w-[130px] text-center text-xs font-semibold text-brand-ink">{data.label}</span>
            <button type="button" onClick={() => step(1)} title={`Next ${periodLabel.toLowerCase()}`} className="rounded p-1 text-brand-muted hover:bg-brand-surface"><ChevronRight size={15} /></button>
          </div>
        </div>
      </div>
      {/* Compare baseline picker (Paul 2026-07-07) */}
      {base && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-brand-muted">Compared with</span>
          {period === 'day' ? (
            <div className="inline-flex overflow-hidden rounded-lg border border-brand-border">
              {[['previous', 'Yesterday'], ['same_dow', 'Same day last week'], ['same_dow_4wk', 'Same day 4 weeks ago']].map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => setBaseKey(k)}
                  className={`px-2.5 py-1 font-medium transition ${baseKey === k ? 'bg-brand-primary text-white' : 'bg-white text-brand-muted hover:bg-brand-surface'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          ) : (
            <span className="font-medium text-brand-ink">{base.label}</span>
          )}
          {/* Quote the RAW count — it used to quote the net-of-cancellation figure ("10 bookings then")
              while the card beside it showed the raw one, so the two never reconciled. Paul 2026-07-14. */}
          <span className="text-brand-muted">
            · {base.total ?? 0} bookings then{base.total_cancelled ? ` (${base.total_actual} net)` : ''}
          </span>
        </div>
      )}

      {/* End-of-day sanity check (Paul 2026-07-02): big number = ACTUAL (net of same-period cancellations);
          when any were cancelled the sub spells out "N booked · M cancelled" so a booking that was made
          today and then cancelled is counted as cancelled rather than silently disappearing. The ▲/▼
          badge (Paul 2026-07-07) is the delta vs the chosen comparison period. */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        {/* The badge must compare the SAME number the card prints. It used to show the raw total (22)
            over a delta computed on the net-of-cancellation figure (20 vs 10) — Paul 2026-07-14. */}
        <FunnelStat label="Bookings made" value={data.total ?? 0}
          sub={[data.total_cancelled ? `${data.total_cancelled} cancelled · ${data.total_actual} actual` : 'entered this period', data.rebooked ? `${data.rebooked} moved (not counted)` : null].filter(Boolean).join(' · ')}
          delta={delta(data.total, base?.total)} baseLabel={base?.label} />
        <FunnelStat label="New patients" value={curNew}
          sub={data.new_cancelled ? `${data.new_count} booked · ${data.new_cancelled} cancelled` : 'first booking'}
          color="text-brand-primary" delta={delta(curNew, baseNew)} baseLabel={base?.label} />
        <FunnelStat label="Existing" value={curExisting}
          sub={data.existing_cancelled ? `${data.existing_count} booked · ${data.existing_cancelled} cancelled` : 'returning'}
          delta={delta(curExisting, baseExisting)} baseLabel={base?.label} />
        <FunnelStat label="Via WhatsApp" value={data.whatsapp ?? 0} sub="AI line" color="text-brand-success"
          delta={delta(data.whatsapp, base?.whatsapp)} baseLabel={base?.label} />
        <FunnelStat label="Direct" value={data.direct ?? 0} sub="call / email / walk-in"
          delta={delta(data.direct, base?.direct)} baseLabel={base?.label} />
      </div>

      {/* New-patient mix — what KIND of new patients we're winning (Paul 2026-07-07) */}
      <div className="mb-5">
        <p className="mb-2 text-[10px] uppercase tracking-[0.14em] text-brand-muted">New patient mix</p>
        <div className="grid grid-cols-3 gap-3">
          <FunnelStat label="Emergency" value={nt.emergency ?? 0} sub="pain / trauma" color="text-brand-danger"
            delta={delta(nt.emergency, bt.emergency)} baseLabel={base?.label} />
          <FunnelStat label="Cosmetic" value={nt.cosmetic ?? 0} sub="whitening / aesthetic" color="text-brand-warning"
            delta={delta(nt.cosmetic, bt.cosmetic)} baseLabel={base?.label} />
          <FunnelStat label="General" value={nt.general ?? 0} sub="check-ups / dentistry"
            delta={delta(nt.general, bt.general)} baseLabel={base?.label} />
        </div>
      </div>

      {/* Like-for-like comparison — the ▲/▼ badges above, spelled out as line items (Paul 2026-07-14) */}
      {base && (
        <div className="mb-5">
          <p className="mb-2 text-[10px] uppercase tracking-[0.14em] text-brand-muted">
            {data.date_label || data.label} vs {base.kind_label || 'baseline'}{base.date_label ? ` — ${base.date_label}` : ''}
          </p>
          <ComparisonTable data={data} base={base} />
        </div>
      )}
      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-brand-muted">
                <th className="py-1 pr-3 font-medium">Booked at</th>
                <th className="py-1 pr-3 font-medium">Patient</th>
                <th className="py-1 pr-3 font-medium">Type</th>
                <th className="py-1 pr-3 font-medium">Channel</th>
                <th className="py-1 pr-3 font-medium">For</th>
                <th className="py-1 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-t border-brand-border/60 ${r.cancelled ? 'bg-red-50/40' : ''} ${r.rebook ? 'opacity-60' : ''}`}>
                  <td className={`py-1.5 pr-3 font-medium whitespace-nowrap ${r.cancelled ? 'text-brand-muted line-through' : 'text-brand-ink'}`}>{period !== 'day' ? `${r.booked_on} ` : ''}{r.booked_at}</td>
                  <td className="py-1.5 pr-3">
                    <span className={r.cancelled ? 'text-brand-muted line-through' : 'text-brand-ink'}>{r.patient}</span>
                    {r.cancelled && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-700">Cancelled</span>}
                    {/* A moved visit is not a new booking (Paul 2026-09-11): the rebook is listed but not counted; its original counts as moved, not cancelled. */}
                    {r.rebook && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700" title={`Replaces the appointment on ${r.rebook_from || '—'} — not counted as a new booking`}>Rebooked · not counted</span>}
                    {r.moved_to && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">Moved → {r.moved_to}</span>}
                  </td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${r.new ? 'bg-brand-primary/10 text-brand-primary' : 'bg-gray-100 text-gray-600'}`}>{r.new ? 'New' : 'Existing'}</span>
                    {r.new && r.type && (
                      <span className={`ml-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${TYPE_BADGE[r.type] || 'bg-gray-100 text-gray-600'}`}>{r.type}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${r.channel === 'whatsapp' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'}`}>{r.channel === 'whatsapp' ? 'WhatsApp' : 'Direct'}</span>
                  </td>
                  <td className="py-1.5 pr-3 text-brand-muted whitespace-nowrap">{r.for_date} {r.for_time}</td>
                  <td className="py-1.5 text-brand-muted">
                    {r.cancelled ? (
                      <div>
                        <span className="font-medium text-brand-danger">{r.cancellation_reason || 'No reason recorded'}</span>
                        {r.reason && <span className="block text-[11px] text-brand-muted/70">was: {r.reason}</span>}
                      </div>
                    ) : (r.reason || '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="py-3 text-center text-xs text-brand-muted">No bookings were made in this period ({data.label}).</p>
      )}
    </div>
  )
}

// current − baseline, or null when there's nothing to compare against.
function delta(cur, base) {
  if (base == null || cur == null) return null
  return cur - base
}

// ▲/▼ badge vs the comparison baseline. Green up / red down for volume metrics; flat "—" when equal.
function DeltaBadge({ delta, baseLabel }) {
  if (delta == null) return null
  const up = delta > 0
  const flat = delta === 0
  const cls = flat ? 'text-brand-muted' : up ? 'text-brand-success' : 'text-brand-danger'
  const arrow = flat ? '—' : up ? '▲' : '▼'
  return (
    <span className={`text-[11px] font-semibold ${cls}`} title={baseLabel ? `vs ${baseLabel}` : undefined}>
      {arrow}{flat ? '' : ` ${Math.abs(delta)}`}
    </span>
  )
}

// Side-by-side comparison, spelled out (Paul 2026-07-14). A ▲/▼ badge on its own made him infer WHICH
// two periods he was looking at and on what basis, so he couldn't trust it. Every metric is now a line
// item: what that period was (with its date), what this period is (with its date), and the difference.
// Basis is stated per row because the units genuinely differ — "bookings" count ENTRIES in the diary,
// "patients" count DISTINCT PEOPLE net of cancellations, which is why new + existing needn't equal
// bookings made (one person booking twice is two bookings but one patient).
function ComparisonTable({ data, base }) {
  if (!base) return null
  const nt = data.new_by_type || {}
  const bt = base.new_by_type || {}
  const n = (v) => v ?? 0
  const rows = [
    { label: 'Bookings made', basis: 'entries into the diary', was: n(base.total), now: n(data.total) },
    { label: '— of which cancelled', basis: 'since cancelled', was: n(base.total_cancelled), now: n(data.total_cancelled), goodWhenDown: true, indent: true },
    { label: 'Net bookings', basis: 'still standing', was: n(base.total_actual), now: n(data.total_actual) },
    { label: 'New patients', basis: 'distinct people, first-ever booking', was: n(base.new_actual ?? base.new_count), now: n(data.new_actual ?? data.new_count), strong: true },
    { label: 'Existing patients', basis: 'distinct people, returning', was: n(base.existing_actual ?? base.existing_count), now: n(data.existing_actual ?? data.existing_count) },
    { label: 'Via WhatsApp', basis: 'bookings on the AI line', was: n(base.whatsapp), now: n(data.whatsapp), strong: true },
    { label: 'Direct', basis: 'bookings by call / email / walk-in', was: n(base.direct), now: n(data.direct) },
    { label: 'New — emergency', basis: 'pain / trauma', was: n(bt.emergency), now: n(nt.emergency), indent: true },
    { label: 'New — cosmetic', basis: 'whitening / aesthetic', was: n(bt.cosmetic), now: n(nt.cosmetic), indent: true },
    { label: 'New — general', basis: 'check-ups / dentistry', was: n(bt.general), now: n(nt.general), indent: true },
  ]
  const Head = ({ kind, date, align = 'text-right' }) => (
    <th className={`py-1.5 px-3 font-medium ${align}`}>
      <span className="block text-[11px] font-semibold text-brand-ink">{date}</span>
      <span className="block text-[10px] font-normal text-brand-muted">{kind}</span>
    </th>
  )
  return (
    <div className="mb-5 overflow-x-auto rounded-lg border border-brand-accent/60 bg-brand-surface/20">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-brand-accent/50 text-brand-muted">
            <th className="py-1.5 px-3 font-medium">Metric</th>
            <Head kind={base.kind_label || 'baseline'} date={base.date_label || base.label} />
            <Head kind={data.kind_label || 'this period'} date={data.date_label || data.label} />
            <th className="py-1.5 px-3 text-right font-medium">Difference</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const d = r.now - r.was
            const flat = d === 0
            const good = r.goodWhenDown ? d < 0 : d > 0
            const cls = flat ? 'text-brand-muted' : good ? 'text-brand-success' : 'text-brand-danger'
            return (
              <tr key={r.label} className="border-t border-brand-border/40">
                <td className={`py-1.5 px-3 ${r.indent ? 'pl-6 text-brand-muted' : 'text-brand-ink'} ${r.strong ? 'font-semibold' : ''}`}>
                  {r.label}
                  <span className="ml-1.5 text-[10px] text-brand-muted">{r.basis}</span>
                </td>
                <td className="py-1.5 px-3 text-right tabular-nums text-brand-muted">{r.was}</td>
                <td className={`py-1.5 px-3 text-right tabular-nums ${r.strong ? 'font-semibold text-brand-ink' : 'text-brand-ink'}`}>{r.now}</td>
                <td className={`py-1.5 px-3 text-right tabular-nums font-semibold ${cls}`}>
                  {flat ? '—' : `${d > 0 ? '+' : '−'}${Math.abs(d)}`}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function FunnelStat({ label, value, sub, color = 'text-brand-ink', delta = null, baseLabel }) {
  return (
    <div className="rounded-lg border border-brand-accent/60 bg-brand-surface/30 p-3">
      <p className="text-[10px] uppercase tracking-[0.14em] text-brand-muted">{label}</p>
      <div className="mt-1 flex items-baseline gap-1.5">
        <p className={`text-2xl font-bold tracking-tight ${color}`}>{value}</p>
        <DeltaBadge delta={delta} baseLabel={baseLabel} />
      </div>
      <p className="mt-0.5 text-[10px] text-brand-muted">{sub}</p>
    </div>
  )
}

function KpiCard({ label, value, color, bg }) {
  return (
    <div className="rounded-xl border border-brand-accent/75 bg-white p-5 shadow-sm">
      <div className={`mb-3 inline-flex rounded-lg px-3 py-2 text-xs font-semibold ${bg} ${color}`}>
        {label}
      </div>
      <p className={`text-3xl font-bold tracking-tight ${color}`}>{value}</p>
      <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-brand-muted">last 30 days</p>
    </div>
  )
}

function EmptyChart() {
  return (
    <div className="flex h-[220px] items-center justify-center rounded-xl bg-brand-surface/40">
      <p className="text-sm text-brand-muted">No data yet</p>
    </div>
  )
}
